import { useMemo } from "react";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import {
  LessonStatus,
  type AggregatedProgress,
} from "@/components/course/tree/types";

interface EnrollmentProgress {
  modules: Map<string, AggregatedProgress>;
  chapters: Map<string, AggregatedProgress>;
}

const EMPTY: EnrollmentProgress = {
  modules: new Map(),
  chapters: new Map(),
};

export const useEnrollmentProgress = (): EnrollmentProgress => {
  const { courseData, progressSubchapters } = useCourseDetailStore();

  return useMemo(() => {
    if (!courseData || Object.keys(progressSubchapters).length === 0) {
      return EMPTY;
    }

    const chapters = new Map<string, AggregatedProgress>();
    const modules = new Map<string, AggregatedProgress>();

    for (const mod of courseData.structure) {
      if (mod.type !== "module") continue;

      let modTotalLessons = 0;
      let modCompletedLessons = 0;
      let modProgressSum = 0;
      let modTotalChapters = 0;

      for (const chap of mod.children ?? []) {
        if (chap.type !== "chapter") continue;
        modTotalChapters++;

        let chapTotalLessons = 0;
        let chapCompletedLessons = 0;
        let chapProgressSum = 0;

        for (const sub of chap.children ?? []) {
          if (sub.type !== "subchapter") continue;
          chapTotalLessons++;

          const subProgress = progressSubchapters[sub.id];
          const progressPercent = subProgress?.progressPercent ?? 0;
          const status = subProgress?.status ?? LessonStatus.NotStarted;

          chapProgressSum += progressPercent;
          if (status === LessonStatus.Completed) chapCompletedLessons++;
        }

        chapters.set(chap.id, {
          progressPercent:
            chapTotalLessons > 0
              ? Math.round(chapProgressSum / chapTotalLessons)
              : 0,
          totalLessons: chapTotalLessons,
          completedLessons: chapCompletedLessons,
          totalChapters: 1,
        });

        modTotalLessons += chapTotalLessons;
        modCompletedLessons += chapCompletedLessons;
        modProgressSum += chapProgressSum;
      }

      modules.set(mod.id, {
        progressPercent:
          modTotalLessons > 0
            ? Math.round(modProgressSum / modTotalLessons)
            : 0,
        totalLessons: modTotalLessons,
        completedLessons: modCompletedLessons,
        totalChapters: modTotalChapters,
      });
    }

    return { modules, chapters };
  }, [courseData, progressSubchapters]);
};
